import toast from "@/components/ui/toast";
import {
  CreateKeyMessage,
  CreateKeyMessageGroup,
  type CreateKeyMessageGroupVariablesType,
  type CreateKeyMessageVariablesType,
  DeleteKeyMessage,
  DeleteKeyMessageGroup,
  GetKeyMessages,
  GetKeyMessagesAndGroups,
  type GetKeyMessagesAndGroupsVariablesType,
  type GetKeyMessagesVariablesType,
  MergeKeyMessageGroup,
  UpdateKeyMessage,
  UpdateKeyMessageGroup,
  type UpdateKeyMessageGroupVariablesType,
  type UpdateKeyMessageVariablesType,
} from "@/data-access/news-feeds/queries/key-message-queries";
import { client } from "@/lib/urqlProvider";
import { omit } from "@/lib/utils/utils";
import useNewsFeedParamsStore from "@/store/news-feed/params/newsFeed-params.slice";
import type { KeyMessage, KeyMessageGroup } from "./key-message.slice";
import useKeyMessageStore from "./key-message.slice";

const COUNT_LOADING_FALLBACK_MS = 30_000;

let countLoadingFallbackTimeoutId: ReturnType<typeof setTimeout> | null = null;

type KeyMessageState = ReturnType<typeof useKeyMessageStore.getState>;

const normalizeKeyMessageName = (name: string): string => name.trim();

const normalizeKeyMessageGroupId = (
  groupId: KeyMessage["groupId"] | null | undefined,
): string | null => (groupId == null ? null : String(groupId));

/** Returns true when a create would violate the feed/group/name uniqueness constraint. */
const hasDuplicateKeyMessageName = (state: KeyMessageState): boolean => {
  const targetName = normalizeKeyMessageName(state.keyMessageName);
  const targetGroupId = normalizeKeyMessageGroupId(state.selectedGroup?.id);

  return state.keyMessages.some(
    (keyMessage) =>
      normalizeKeyMessageName(keyMessage.name) === targetName &&
      normalizeKeyMessageGroupId(keyMessage.groupId) === targetGroupId,
  );
};

const duplicateKeyMessageNameError = (state: KeyMessageState): string =>
  state.selectedGroup
    ? "A key topic with this name already exists in this group."
    : "A key topic with this name already exists.";

const clearCountLoadingFallbackTimeout = () => {
  if (countLoadingFallbackTimeoutId !== null) {
    clearTimeout(countLoadingFallbackTimeoutId);
    countLoadingFallbackTimeoutId = null;
  }
};

/** Schedules a one-time fallback to clear isCountLoading so spinners never hang if subscription never delivers. */
const scheduleCountLoadingFallbackTimeout = () => {
  clearCountLoadingFallbackTimeout();
  countLoadingFallbackTimeoutId = setTimeout(() => {
    countLoadingFallbackTimeoutId = null;
    useKeyMessageStore.setState((state) => ({
      keyMessages: state.keyMessages.map((m) => ({
        ...m,
        isCountLoading: false,
      })),
      keyMessageGroups: state.keyMessageGroups.map((group) => ({
        ...group,
        keyMessageItems: group.keyMessageItems.map((item) => ({
          ...item,
          isCountLoading: false,
        })),
      })),
    }));
  }, COUNT_LOADING_FALLBACK_MS);
};

export const createKeyMessageGroup = async (
  feedId: string,
): Promise<{ id: string; name: string } | undefined> => {
  const state = useKeyMessageStore.getState();

  const input: CreateKeyMessageGroupVariablesType["input"] = {
    feedId,
    name: state.keyMessageGroupName,
  };

  useKeyMessageStore.setState({ isCreatingKeyMessageGroup: true });

  try {
    const result = await client
      .mutation(CreateKeyMessageGroup, { input })
      .toPromise();

    if (result.error) {
      toast.error("Failed to create key topic group.");
      useKeyMessageStore.setState(
        { isCreatingKeyMessageGroup: false },
        false,
        "create key message group error",
      );
      return undefined;
    }

    const createKeyMessageGroup = result.data?.createKeyMessageGroup;
    if (
      !createKeyMessageGroup ||
      createKeyMessageGroup.__typename !== "KeyMessageGroupType"
    ) {
      toast.error("Failed to create key topic group.");
      useKeyMessageStore.setState(
        { isCreatingKeyMessageGroup: false },
        false,
        "create key message group error",
      );
      return undefined;
    }

    const newKeyMessageGroup: KeyMessageGroup = {
      id: createKeyMessageGroup.id,
      name: createKeyMessageGroup.name,
      keyMessageItems: [],
      __typename: "KeyMessageGroupType",
    };

    useKeyMessageStore.setState(
      (state) => ({
        keyMessageGroups: [...state.keyMessageGroups, newKeyMessageGroup],
        isCreatingKeyMessageGroup: false,
        createKeyMessageGroupState: "complete",
      }),
      false,
      "create key message group success",
    );
    toast({
      type: "success",
      title: "New group created!",
      description: `"${createKeyMessageGroup.name}" group has been created and added to your filters.`,
    });
    return omit(createKeyMessageGroup, "__typename") as {
      id: string;
      name: string;
    };
  } catch (error) {
    console.error("Error creating key message group:", error);
    toast.error("An error occurred while creating the key topic group.");
    useKeyMessageStore.setState({ isCreatingKeyMessageGroup: false });
    return undefined;
  }
};

export const createKeyMessage = async (feedId: string): Promise<boolean> => {
  const state = useKeyMessageStore.getState();

  if (state.isCreatingKeyMessage) return false;

  if (hasDuplicateKeyMessageName(state)) {
    toast.error(duplicateKeyMessageNameError(state));
    return false;
  }

  const input: CreateKeyMessageVariablesType["input"] = {
    feedId,
    name: normalizeKeyMessageName(state.keyMessageName),
    searchTerms: state.searchTerm,
    groupId: state.selectedGroup?.id ?? null,
  };

  useKeyMessageStore.setState({ isCreatingKeyMessage: true });

  try {
    const result = await client
      .mutation(CreateKeyMessage, { input })
      .toPromise();

    if (result.error) {
      toast.error("Failed to create key topic.");
      useKeyMessageStore.setState(
        { isCreatingKeyMessage: false },
        false,
        "create key message error",
      );
      return false;
    }

    const createKeyMessage = result.data?.createKeyMessageItem;
    if (
      !createKeyMessage ||
      createKeyMessage.__typename !== "KeyMessageItemType"
    ) {
      toast.error("Failed to create key topic.");
      useKeyMessageStore.setState({ isCreatingKeyMessage: false });
      return false;
    }

    // Add the key message optimistically to the store
    const newKeyMessage: KeyMessage = {
      ...createKeyMessage,
    };

    const groupIdStr =
      createKeyMessage.groupId !== null &&
      createKeyMessage.groupId !== undefined
        ? String(createKeyMessage.groupId)
        : null;

    useKeyMessageStore.setState(
      (currentState) => {
        // Add to keyMessages array
        const updatedKeyMessages = [...currentState.keyMessages, newKeyMessage];

        // Add to the appropriate group's keyMessageItems
        const updatedGroups = currentState.keyMessageGroups.map((group) => {
          if (groupIdStr && group.id === groupIdStr) {
            return {
              ...group,
              keyMessageItems: [...group.keyMessageItems, newKeyMessage],
            };
          }
          return group;
        });

        return {
          keyMessages: updatedKeyMessages,
          keyMessageGroups: updatedGroups,
          isCreatingKeyMessage: false,
          createKeyMessageState: "complete",
        };
      },
      false,
      "create key message success",
    );

    toast({
      type: "success",
      title: "Key topic created!",
      description: `"${createKeyMessage.name}" has been created and added to your filters.`,
    });

    return true;
  } catch (error) {
    console.error("Error creating key message:", error);
    toast.error("An error occurred while creating the key topic.");
    useKeyMessageStore.setState({ isCreatingKeyMessage: false });
    return false;
  }
};

export const fetchKeyMessages = async (
  filters: GetKeyMessagesVariablesType["filters"],
) => {
  useKeyMessageStore.setState({ isFetchingKeyMessages: true });

  try {
    const result = await client.query(GetKeyMessages, { filters }).toPromise();

    if (result.error) {
      toast.error("Failed to fetch key topics.");
      useKeyMessageStore.setState(
        { isFetchingKeyMessages: false },
        false,
        "fetch key messages error",
      );
      return;
    }

    if (!result.data?.keyMessageItemsV2) {
      toast.error("Failed to fetch key topics.");
      useKeyMessageStore.setState({ isFetchingKeyMessages: false });
      return;
    }

    useKeyMessageStore.setState(
      {
        keyMessages: result.data.keyMessageItemsV2,
        isFetchingKeyMessages: false,
      },
      false,
      "fetch key messages success",
    );
  } catch (error) {
    console.error("Error fetching key messages:", error);
    toast.error("An error occurred while fetching key topics.");
    useKeyMessageStore.setState(
      { isFetchingKeyMessages: false },
      false,
      "fetch key messages error",
    );
  }
};

export const updateKeyMessageGroup = async (
  input: UpdateKeyMessageGroupVariablesType["input"],
) => {
  try {
    const result = await client
      .mutation(UpdateKeyMessageGroup, { input })
      .toPromise();

    if (result.error) {
      toast.error(`Failed to update ${input.name} group.`);
      return;
    }

    const updatedGroup = result.data?.updateKeyMessageGroup;

    if (!updatedGroup || updatedGroup.__typename !== "KeyMessageGroupType") {
      toast.error(`Failed to update ${input.name} group.`);
      return;
    }

    // Update the store directly with the new group data
    useKeyMessageStore.setState((state) => ({
      ...state,
      keyMessageGroups: state.keyMessageGroups.map((group) =>
        group.id === updatedGroup.id
          ? {
              ...group,
              name: updatedGroup.name,
            }
          : group,
      ),
      isEditingKeyMessageGroup: false,
      selectedGroup: null,
    }));

    toast({
      type: "success",
      title: "Key topic group updated!",
      description: `Key topic group has been updated to "${updatedGroup.name}".`,
    });
  } catch (error) {
    console.error("Error updating key message group:", error);
    toast.error(
      `An error occurred while updating the ${input.name} key topic group.`,
    );
  }
};

export const updateKeyMessage = async (
  input: UpdateKeyMessageVariablesType["input"],
): Promise<boolean> => {
  try {
    const result = await client
      .mutation(UpdateKeyMessage, { input })
      .toPromise();

    if (result.error) {
      toast.error(`Failed to update ${input.name} key topic.`);
      return false;
    }

    const updatedMessage = result.data?.updateKeyMessageItem;

    if (!updatedMessage || updatedMessage.__typename !== "KeyMessageItemType") {
      toast.error(`Failed to update ${input.name} key topic.`);
      return false;
    }

    toast({
      type: "success",
      title: "Key topic updated!",
      description: `Key topic "${updatedMessage.name}" has been updated.`,
    });
    return true;
  } catch (error) {
    console.error("Error updating key message:", error);
    toast.error(
      `An error occurred while updating the ${input.name} key topic.`,
    );
    return false;
  }
};

export const fetchKeyMessagesAndGroups = async (
  // feedId is string (ID) in the app but KeyMessageGroupByFeedFilter.feedId is still Int!
  { feedId }: { feedId: string },
) => {
  const filters: GetKeyMessagesAndGroupsVariablesType["filters"] = {
    feedId,
  };
  useKeyMessageStore.setState({ isFetchingKeyMessagesAndGroups: true });

  try {
    const result = await client
      .query(
        GetKeyMessagesAndGroups,
        { filters },
        {
          requestPolicy: "cache-and-network",
        },
      )
      .toPromise();

    if (result.error) {
      toast.error("Failed to fetch key topics and groups.");
      useKeyMessageStore.setState(
        {
          keyMessages: [],
          keyMessageGroups: [],
          isFetchingKeyMessagesAndGroups: false,
        },
        false,
        "fetch key messages and groups error",
      );
      return;
    }

    if (!result.data?.keyMessageGroups) {
      toast.error("Failed to fetch key topics and groups.");
      useKeyMessageStore.setState({
        keyMessages: [],
        keyMessageGroups: [],
        isFetchingKeyMessagesAndGroups: false,
      });
      return;
    }

    const keyMessageGroups = result.data.keyMessageGroups;
    const keyMessages = keyMessageGroups.flatMap((group) =>
      group.keyMessageItems.map((item) => ({
        ...item,
        isCountLoading: true,
      })),
    );

    useKeyMessageStore.setState(
      {
        keyMessageGroups: keyMessageGroups.map((group) => ({
          ...group,
          keyMessageItems: group.keyMessageItems.map((item) => ({
            ...item,
            isCountLoading: true,
          })),
        })),
        keyMessages,
        isFetchingKeyMessagesAndGroups: false,
      },
      false,
      "fetch key messages and groups success",
    );
    scheduleCountLoadingFallbackTimeout();
  } catch (error) {
    console.error("Error fetching key messages and groups:", error);
    toast.error("An error occurred while fetching key topics and groups.");
    useKeyMessageStore.setState({
      keyMessages: [],
      keyMessageGroups: [],
      isFetchingKeyMessagesAndGroups: false,
    });
  }
};

export const deleteKeyMessageGroup = async (
  groupId: string,
  feedId: string,
  groupName: string,
) => {
  useKeyMessageStore.setState({ isDeletingKeyMessageGroup: true });

  try {
    const result = await client
      .mutation(DeleteKeyMessageGroup, { groupId, feedId })
      .toPromise();

    if (result.error) {
      toast.error(`Failed to delete ${groupName} group.`);
      useKeyMessageStore.setState({ isDeletingKeyMessageGroup: false });
      return;
    }

    const deletedGroup = result.data?.deleteKeyMessageGroup;
    if (!deletedGroup || deletedGroup.__typename !== "KeyMessageGroupType") {
      toast.error(`Failed to delete ${groupName} group.`);
      useKeyMessageStore.setState({ isDeletingKeyMessageGroup: false });
      return;
    }

    // Get all key message IDs that will be deleted (from the group being deleted)
    const state = useKeyMessageStore.getState();
    const groupToDelete = state.keyMessageGroups.find(
      (group) => group.id === groupId,
    );
    const deletedKeyMessageIds = new Set(
      groupToDelete?.keyMessageItems.map((km: KeyMessage) => km.id) || [],
    );

    // Update key message store - remove the group and its key messages
    useKeyMessageStore.setState((state) => ({
      keyMessageGroups: state.keyMessageGroups.filter(
        (group) => group.id !== groupId,
      ),
      keyMessages: state.keyMessages.filter(
        (message) => message.groupId !== groupId,
      ),
    }));

    // Clean up params store - remove deleted key message IDs from selectedFilters
    const paramsState = useNewsFeedParamsStore.getState();
    const currentKeyMessages =
      paramsState.selectedFilters.get("KEY_MESSAGE") || [];
    const updatedKeyMessages = currentKeyMessages.filter(
      (id: string) => !deletedKeyMessageIds.has(id),
    );

    // Only update if there were changes
    if (updatedKeyMessages.length !== currentKeyMessages.length) {
      const newFilters = new Map(paramsState.selectedFilters);
      if (updatedKeyMessages.length > 0) {
        newFilters.set("KEY_MESSAGE", updatedKeyMessages);
      } else {
        newFilters.delete("KEY_MESSAGE");
      }
      useNewsFeedParamsStore.setState({ selectedFilters: newFilters });
    }

    toast({
      type: "neutral",
      title: "Key topic group deleted",
      description: `"${groupName}" group and all associated key topics are successfully deleted.`,
    });
    useKeyMessageStore.setState({ isDeletingKeyMessageGroup: false });
  } catch (error) {
    console.error("Error deleting key message group:", error);
    toast.error("An error occurred while deleting the key topic group.");
    useKeyMessageStore.setState({ isDeletingKeyMessageGroup: false });
  }
};

export const deleteKeyMessage = async (
  itemId: string,
  feedId: string,
  itemName: string,
) => {
  useKeyMessageStore.setState({ isDeletingKeyMessage: true });

  try {
    const result = await client
      .mutation(DeleteKeyMessage, { itemId, feedId })
      .toPromise();

    if (result.error) {
      toast.error(`Failed to delete ${itemName} key topic.`);
      useKeyMessageStore.setState({ isDeletingKeyMessage: false });
      return;
    }

    const deletedMessage = result.data?.deleteKeyMessageItem;
    if (!deletedMessage || deletedMessage.__typename !== "KeyMessageItemType") {
      toast.error(`Failed to delete ${itemName} key topic.`);
      useKeyMessageStore.setState({ isDeletingKeyMessage: false });
      return;
    }

    // Update key message store - remove the key message
    useKeyMessageStore.setState((state) => ({
      keyMessages: state.keyMessages.filter(
        (km: KeyMessage) => km.id !== itemId,
      ),
      keyMessageGroups: state.keyMessageGroups.map((group) => ({
        ...group,
        keyMessageItems: group.keyMessageItems.filter(
          (km: KeyMessage) => km.id !== itemId,
        ),
      })),
    }));

    // Clean up params store - remove deleted key message ID from selectedFilters
    const paramsState = useNewsFeedParamsStore.getState();
    const currentKeyMessages =
      paramsState.selectedFilters.get("KEY_MESSAGE") || [];

    // Only update if this key message was selected
    if (currentKeyMessages.includes(itemId)) {
      const updatedKeyMessages = currentKeyMessages.filter(
        (id: string) => id !== itemId,
      );
      const newFilters = new Map(paramsState.selectedFilters);

      if (updatedKeyMessages.length > 0) {
        newFilters.set("KEY_MESSAGE", updatedKeyMessages);
      } else {
        newFilters.delete("KEY_MESSAGE");
      }

      useNewsFeedParamsStore.setState({ selectedFilters: newFilters });
    }

    toast({
      type: "neutral",
      title: "Key topic deleted",
      description: `"${itemName}" has been deleted.`,
    });
    useKeyMessageStore.setState({ isDeletingKeyMessage: false });
  } catch (error) {
    console.error("Error deleting key message:", error);
    toast.error("An error occurred while deleting the key topic.");
    useKeyMessageStore.setState({ isDeletingKeyMessage: false });
  }
};

export const mergeKeyMessageGroup = async (
  feedId: string,
  fromGroupId: string,
  toGroupId: string,
  fromGroupName: string,
  toGroupName: string,
) => {
  useKeyMessageStore.setState({ isMergingKeyMessageGroup: true });
  try {
    const result = await client
      .mutation(MergeKeyMessageGroup, {
        feedId,
        fromId: fromGroupId,
        toId: toGroupId,
      })
      .toPromise();

    if (result.error) {
      toast.error(`Failed to merge "${fromGroupName}" into "${toGroupName}".`);
      useKeyMessageStore.setState({ isMergingKeyMessageGroup: false });
      return;
    }

    const mergedGroup = result.data?.mergeKeyMessageGroup;
    if (!mergedGroup || mergedGroup.__typename !== "KeyMessageGroupType") {
      toast.error(`Failed to merge "${fromGroupName}" into "${toGroupName}".`);
      useKeyMessageStore.setState({ isMergingKeyMessageGroup: false });
      return;
    }

    // Update store directly - only update groups
    useKeyMessageStore.setState((state) => ({
      keyMessageGroups: state.keyMessageGroups.filter(
        (group) => group.id !== fromGroupId,
      ),
      isMergingKeyMessageGroup: false,
    }));

    toast({
      type: "success",
      title: "Groups merged successfully",
      description: `"${fromGroupName}" has been merged into "${toGroupName}".`,
    });
  } catch (error) {
    console.error("Error merging key message groups:", error);
    toast.error("An error occurred while merging the key topic groups.");
    useKeyMessageStore.setState({ isMergingKeyMessageGroup: false });
  }
};

/**
 * Sets isCountLoading for all key message items. Call when subscription filters change
 * so the UI shows spinners until new counts arrive.
 */
export const setAllKeyMessageCountsLoading = (loading: boolean) => {
  useKeyMessageStore.setState((state) => ({
    keyMessages: state.keyMessages.map((m) => ({
      ...m,
      isCountLoading: loading,
    })),
    keyMessageGroups: state.keyMessageGroups.map((group) => ({
      ...group,
      keyMessageItems: group.keyMessageItems.map((item) => ({
        ...item,
        isCountLoading: loading,
      })),
    })),
  }));
  if (loading) {
    scheduleCountLoadingFallbackTimeout();
  } else {
    clearCountLoadingFallbackTimeout();
  }
};

export const updateKeyMessageFromSubscription = (
  updatedKeyMessage: KeyMessage | null | undefined,
) => {
  if (!updatedKeyMessage) return;

  const groupIdStr =
    updatedKeyMessage.groupId !== null &&
    updatedKeyMessage.groupId !== undefined
      ? String(updatedKeyMessage.groupId)
      : null;

  // Always ensure isCountLoading is false for the updated message so spinner doesn't hang
  const updatedMessageWithLoading = {
    ...updatedKeyMessage,
    isCountLoading: false,
  };

  // Defer state update to avoid "Cannot update a component while rendering" error
  // when subscription updates arrive during React render phase
  queueMicrotask(() => {
    useKeyMessageStore.setState((state) => {
      const updatedKeyMessages = state.keyMessages.some(
        (m) => m.id === updatedMessageWithLoading.id,
      )
        ? state.keyMessages.map((message) =>
            message.id === updatedMessageWithLoading.id
              ? updatedMessageWithLoading
              : message,
          )
        : [...state.keyMessages, updatedMessageWithLoading];

      // Note: Success toasts for user-initiated actions are handled in the respective action functions
      // WebSocket updates should not show success toasts to avoid duplicates

      const targetGroupExists = groupIdStr
        ? state.keyMessageGroups.some((group) => group.id === groupIdStr)
        : false;

      let updatedGroups = [...state.keyMessageGroups];

      if (groupIdStr && !targetGroupExists) {
        console.warn(
          `Received update for unknown group ${groupIdStr}, creating temporary ungrouped group.`,
        );
        updatedGroups = [
          ...updatedGroups,
          {
            id: groupIdStr,
            name: "Ungrouped",
            keyMessageItems: [updatedMessageWithLoading],
            __typename: "KeyMessageGroupType",
          },
        ];

        return {
          keyMessages: updatedKeyMessages,
          keyMessageGroups: updatedGroups,
        };
      }

      const updatedGroupsFinal = updatedGroups.map((group) => {
        const isTargetGroup = group.id === groupIdStr;
        const wasInThisGroup = group.keyMessageItems.some(
          (item) => item.id === updatedMessageWithLoading.id,
        );

        let newGroupItems = group.keyMessageItems;

        if (wasInThisGroup && !isTargetGroup) {
          newGroupItems = group.keyMessageItems.filter(
            (item) => item.id !== updatedMessageWithLoading.id,
          );
        }

        if (isTargetGroup) {
          if (wasInThisGroup) {
            newGroupItems = group.keyMessageItems.map((item) =>
              item.id === updatedMessageWithLoading.id
                ? updatedMessageWithLoading
                : item,
            );
          } else {
            newGroupItems = [
              ...group.keyMessageItems,
              updatedMessageWithLoading,
            ];
          }
        }

        return {
          ...group,
          keyMessageItems: newGroupItems,
        };
      });

      return {
        keyMessages: updatedKeyMessages,
        keyMessageGroups: updatedGroupsFinal,
      };
    });
  });
};
